import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { injectIntl } from 'react-intl';

import Modal, { ModalBody, ModalFooter } from 'Components/Modal';
import { Button } from 'Components';
import Pill from 'Components/Pill';
import { NotificationManager } from 'Components/Notifications';

import {
    mapGeofenceOpenDeleteModal,
    mapGeofenceDeleteGeofences,
    addUpdateGeofence,
    clearRedux
} from 'Redux/actions';

import './style.scss';

const GeofenceDelete = (props) => {
  const dispatch = useDispatch();
  const { intl,cleanSearch } = props;
  const { messages } = intl;

  const {
    open,
    geofences,
    response,
    loadingResponse,
    deleteAll,
    unselectedRows,
    filterTags
  } = useSelector((state) => state.realtimeMapGeofenceDeleteModuleRedux);

  useEffect(() => {
    return () => {
      dispatch(clearRedux("MAP_GEOFENCE_DELETE_GEOFENCE"));
    }
  }, []);

  useEffect(() => {
    if (response && open) {
      if (response.error) {
        NotificationManager.error(
          response.message || messages['errorMessage'],
          messages['error'],
          3000,
          null,
          null,
          'warning',
        );
        dispatch(clearRedux("MAP_GEOFENCE_DELETE_GEOFENCE", { response: null }));
      } else {
        NotificationManager.success(
          geofences && geofences.length > 1 ? messages['mapGeofenceDeleteGeofence_success'] : messages['mapGeofenceDeleteGeofence_success_one'],
          messages['success'],
          3000,
          null,
          null,
          'success',
        );

        dispatch(addUpdateGeofence("remove", [...geofences]));
        cleanSearch();

        handleClose();
      }
    }
  }, [response, open]);

  const handleClose = useCallback(() => {
    dispatch(mapGeofenceOpenDeleteModal(false));
    dispatch(clearRedux("MAP_GEOFENCE_DELETE_GEOFENCE"));
  });

  const AllGeofences = React.createElement(
    'a',
    {
      className: 'geofence-delete-all'
    },
    messages['mapGeofenceDeleteGeofence_remove_all_geofence_keyword']
  );

  const confirmationMessage = unselectedRows && unselectedRows.length > 0
    ? messages['mapGeofenceDeleteGeofence_remove_all_except']
    : '';

  // When a tag filter is active, classify each tag:
  // - No deselected rows belonging to it → full tag pill
  // - Some deselected rows belonging to it → show individual selected geofences instead
  const getFilterTagsDisplay = () => {
    if (!filterTags || filterTags.length === 0) return { tagPills: [], individualGeofences: [] };

    if (!unselectedRows || unselectedRows.length === 0) {
      return { tagPills: filterTags, individualGeofences: [] };
    }

    const tagPills = [];
    const individualGeofences = [];

    filterTags.forEach((tag) => {
      const hasUnselected = unselectedRows.some((row) => {
        if (!row.tagsId) return false;
        return row.tagsId.split(',').includes(tag.id.toString());
      });

      if (!hasUnselected) {
        tagPills.push(tag);
      } else {
        const tagGeofences = (geofences || []).filter((g) => {
          if (!g.tagsId) return false;
          return g.tagsId.split(',').includes(tag.id.toString());
        });
        individualGeofences.push(...tagGeofences);
      }
    });

    // De-duplicate geofences that belong to multiple selected tags
    const seen = new Set();
    const dedupedGeofences = individualGeofences.filter((g) => {
      if (seen.has(g.id)) return false;
      seen.add(g.id);
      return true;
    });

    return { tagPills, individualGeofences: dedupedGeofences };
  };

  const { tagPills, individualGeofences } = getFilterTagsDisplay();

  return (
    <Modal
      className="modal-top-0"
      handleClose={handleClose}
      headerStyle={{ top: 0 }}
      loading={loadingResponse}
      open={open}
      position="right"
      size="extra-small"
      title={messages["mapGeofenceDeleteGeofence_title"]}
    >
      <ModalBody>
        <div style={{ padding: '18px 24px 24px 24px', height: 650 }}>

          <div>
            {
              filterTags && filterTags.length > 0
              ?
              <p className='geofence-delete-message'>
                {messages['mapGeofenceDeleteGeofence_remove_filtered_tags']}
              </p>
              :
              deleteAll
              ?
              <p className='geofence-delete-message'>
                {messages['mapGeofenceDeleteGeofence_remove_all']} {AllGeofences} {` ${confirmationMessage}?`}
              </p>
              :
              <p>
                {
                  (geofences && geofences.length > 1)
                  ?
                  messages["mapGeofenceDeleteGeofence_detail"]
                  :
                  messages["mapGeofenceDeleteGeofence_detail_one"]
                }
              </p>
            }
            <p>
              {messages["mapGeofenceDeleteGeofence_warning"]}
            </p>
          </div>

          <div style={{ padding: 10 }}>
            {
              filterTags && filterTags.length > 0
              ?
              <div className="geofence-delete-filter-tags">
                {
                  tagPills.map((tag, index) => (
                    <Pill
                      key={index}
                      item={tag}
                      textField="name"
                      colorField="color"
                      clickable={false}
                      style={{ margin: '4px 4px 4px 0' }}
                    />
                  ))
                }
                {
                  individualGeofences.length > 0 && (
                    <ul style={{ width: '100%', margin: '8px 0 0 0', paddingLeft: 20 }}>
                      {individualGeofences.map((geofence, index) => (
                        <li key={index}>
                          <span>{geofence.name}</span>
                        </li>
                      ))}
                    </ul>
                  )
                }
              </div>
              :
              deleteAll ?
                <ul>
                  {
                    unselectedRows
                    &&
                    unselectedRows.map((geofence, index) => {
                      return (
                        <li key={index}>
                          <span>{geofence.name}</span>
                        </li>
                      )
                    })
                  }
                </ul>
              :
              (
              <ul>
                {
                  geofences
                  &&
                  geofences.map((geofence, index) => {
                    return (
                      <li key={index}>
                        <span>{geofence.name}</span>
                      </li>
                    )
                  })
                }
              </ul>
              )
            }
          </div>

        </div>
      </ModalBody>

      <ModalFooter style={{ bottom: 50 }}>
        <Button
          disabled={loadingResponse}
          onClick={handleClose}
          className="btn-cancel"
        >
          {messages['cancel']}
        </Button>

        <Button
          disabled={loadingResponse}
          onClick={(e) => {
            if (e) e.preventDefault();
            dispatch(mapGeofenceDeleteGeofences(geofences, deleteAll, unselectedRows));
          }}
          className="btn-red"
        >
          {messages['delete']}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default injectIntl(GeofenceDelete);